import { ServerInstanceInfo, ServerInstanceOrder } from '@/api/type-server'
import { PortProtocol } from '@/consts/machine'
import { formatOldMachineName, getOldHostType } from '@/utils/node'

export enum InstanceStatus {
  InvalidStatus = 0,
  Pending,
  Running,
  Exited,
  Finished,
  // Rebooted, // removed
  Canceling = 6,
  UnAvail,
  Pausing,
  Paused,
  Resuming,
  Snapshotted,
  ExitSnapshotted,
  Migrating,
  // Unknown status,
  SchedInit = 15,
  SchedQueued,
}

export const RunningStatuses = [
  InstanceStatus.Pending,
  InstanceStatus.Running,
  InstanceStatus.UnAvail,
  InstanceStatus.SchedInit,
  InstanceStatus.SchedQueued,
]

export const SnapshottingStatuses = [
  InstanceStatus.Pausing,
  InstanceStatus.Paused,
  InstanceStatus.Resuming,
  InstanceStatus.Snapshotted,
  InstanceStatus.ExitSnapshotted,
]

export const ReleasedStatuses = [
  InstanceStatus.Exited,
  InstanceStatus.Finished,
  InstanceStatus.Canceling,
]

export const ActiveStatuses = [
  InstanceStatus.Pending,
  InstanceStatus.Running,
  InstanceStatus.Exited,
  InstanceStatus.Finished,
]

export const instanceStatusMap = {
  [InstanceStatus.InvalidStatus]: {
    label: 'Unknown',
    style: 'status-grey',
  },
  [InstanceStatus.Pending]: {
    label: 'Pending',
    style: 'text-btn-secondary',
  },
  [InstanceStatus.Running]: {
    label: 'Running',
    style: 'text-success',
  },
  [InstanceStatus.Exited]: {
    label: 'Terminated',
    style: 'text-secondary',
  },
  [InstanceStatus.Finished]: {
    label: 'Terminated',
    style: 'text-secondary',
  },
  [InstanceStatus.Canceling]: {
    label: 'Terminated',
    style: 'text-secondary',
  },
  [InstanceStatus.UnAvail]: {
    label: 'Running',
    style: 'text-success',
  },
  [InstanceStatus.Pausing]: {
    label: 'Suspending',
    style: 'text-warning',
  },
  [InstanceStatus.Paused]: {
    label: 'Paused',
    style: 'text-warning',
  },
  [InstanceStatus.Resuming]: {
    label: 'Resuming',
    style: 'text-success',
  },
  [InstanceStatus.Snapshotted]: {
    label: 'Saved',
    style: 'text-warning',
  },
  [InstanceStatus.ExitSnapshotted]: {
    label: 'Saved',
    style: 'text-secondary',
  },
  [InstanceStatus.Migrating]: {
    label: 'Resetting',
    style: 'text-btn-secondary',
  },
  [InstanceStatus.SchedInit]: {
    label: 'Pending',
    style: 'text-btn-secondary',
  },
  [InstanceStatus.SchedQueued]: {
    label: 'Pending',
    style: 'text-btn-secondary',
  },
}

export interface Port {
  srcPort: number
  destPort: number
  url: string
  protocol: PortProtocol
}

export interface SSHAuth {
  user: string
  password: string
}

export interface InstanceSnapshot {
  id: number
  path: string
  type: number
  status: number
  errno: number
  subject: string
  attributes: {
    isReleaseNode: boolean
    isIsolatedStore: boolean
  }
  ownerName: string
  createTime: { seconds: number }
  OwnerType: number
}

export class Instance {
  id: number
  resID: string
  displayID: string
  domain: number
  status: InstanceStatus
  cost: number
  billType: number
  hardware: ServerInstanceInfo['node']['hardware']
  ports: Port[]
  auth: SSHAuth
  agentID: number
  fromSnapshot?: ServerInstanceInfo['fromSnapshots'][0]
  node: ServerInstanceInfo['node']
  flag: ServerInstanceInfo['flag']
  mountInfos: ServerInstanceInfo['mountInfos']
  teamMountInfos: ServerInstanceInfo['teamMountInfos']
  cmd: ServerInstanceInfo['cmd']
  envs: ServerInstanceInfo['envs']
  netGroup?: NetGroup
  nats?: ServerInstanceInfo['nats']
  eventLog?: ServerInstanceInfo['eventLog']
  snapshots: InstanceSnapshot[]
  order: ServerInstanceOrder | null

  constructor(props: ServerInstanceInfo) {
    const { node, resID } = props
    const { user, password } = props.sshAuths?.[0]?.BaseOn?.Cred ?? {}

    this.id = node.id
    this.resID = resID
    this.displayID = props.displayID
    this.status = props.status
    this.domain = node.domain
    this.cost = props.amount
    this.billType = props.billType
    this.hardware = node.hardware
    this.ports = node.ports
    this.auth = { user, password }
    this.agentID = props.agentID
    this.fromSnapshot = props.fromSnapshots?.[0]
    this.node = props.node
    this.flag = props.flag
    this.mountInfos = props.mountInfos
    this.teamMountInfos = props.teamMountInfos ?? []
    this.cmd = props.cmd
    this.envs = props.envs
    this.netGroup = props.nets?.[0]
    this.nats = props.nats
    this.eventLog = props.eventLog
    this.snapshots = props.snapshots ?? []
    this.order = props.order ?? null
  }

  get rentInfos() {
    return {
      image: this.node.image,
      ports: this.node.ports || [],
      mountInfos: this.mountInfos,
      cmd: this.cmd,
      envs: this.envs,
    }
  }

  get hostType() {
    return getOldHostType(this.hardware)
  }

  get displayTitle() {
    return (
      this.flag?.subject ??
      formatOldMachineName(this.hardware) ??
      this.node?.resource?.gpuName ??
      this.node?.resource?.cpuName.replace(/(\(R\)|@\s.*GHz)/g, '')
    )
  }

  get statusKey() {
    const keyMap: Record<InstanceStatus, string> = {
      [InstanceStatus.InvalidStatus]: 'instance.status.unknown',
      [InstanceStatus.Pending]: 'instance.status.pending',
      [InstanceStatus.Running]: 'instance.status.running',
      [InstanceStatus.Exited]: 'instance.status.terminated',
      [InstanceStatus.Finished]: 'instance.status.terminated',
      [InstanceStatus.Canceling]: 'instance.status.terminated',
      [InstanceStatus.UnAvail]: 'instance.status.running',
      [InstanceStatus.Pausing]: 'instance.status.suspending',
      [InstanceStatus.Paused]: 'instance.status.paused',
      [InstanceStatus.Resuming]: 'instance.status.resuming',
      [InstanceStatus.Snapshotted]: 'instance.status.saved',
      [InstanceStatus.ExitSnapshotted]: 'instance.status.saved',
      [InstanceStatus.Migrating]: 'instance.status.resetting',
      [InstanceStatus.SchedInit]: 'instance.status.pending',
      [InstanceStatus.SchedQueued]: 'instance.status.pending',
    }
    return keyMap[this.status] ?? 'instance.status.unknown'
  }

  get statusText() {
    return instanceStatusMap[this.status]?.label ?? instanceStatusMap[InstanceStatus.Pending]?.label
  }

  get statusTextStyle() {
    return instanceStatusMap[this.status]?.style ?? instanceStatusMap[InstanceStatus.Pending]?.style
  }

  get isReleased() {
    return (
      this.status === InstanceStatus.Exited ||
      this.status === InstanceStatus.Finished ||
      this.status === InstanceStatus.Canceling
    )
  }

  get isRunning() {
    return this.status === InstanceStatus.Running
  }

  get inProgress() {
    return [
      InstanceStatus.Pending,
      InstanceStatus.Canceling,
      InstanceStatus.Pausing,
      InstanceStatus.Resuming,
      InstanceStatus.Migrating,
    ].includes(this.status)
  }

  get canRelease() {
    // FIXME
    return [
      InstanceStatus.Pending,
      InstanceStatus.Running,
      InstanceStatus.UnAvail,
      InstanceStatus.Pausing,
      InstanceStatus.Paused,
      InstanceStatus.Resuming,
      InstanceStatus.Snapshotted,
      InstanceStatus.SchedInit,
      InstanceStatus.SchedQueued,
    ].includes(this.status)
  }

  get canSnapshot() {
    return this.status === InstanceStatus.Running && this.hardware.attributes.isSnapEnabled
  }

  get currentSavingSnapshot(): InstanceSnapshot | undefined {
    if (this.status !== InstanceStatus.Paused) return undefined
    if (this.snapshots.length === 0) return undefined
    return this.snapshots[this.snapshots.length - 1]
  }

  get canCancelSnapshot(): boolean {
    return this.status === InstanceStatus.Paused && this.currentSavingSnapshot !== undefined
  }

  get isSubscription() {
    return this.order !== null
  }

  get billingMethodLabel() {
    return this.isSubscription ? 'Subscription' : 'Pay-as-you-go'
  }

  get subscriptionDeadlineTime(): number | null {
    const seconds = this.order?.deadlineTime?.seconds
    return seconds === undefined ? null : seconds * 1000
  }

  get autoRenewal() {
    return this.order?.autoRenewal ?? false
  }

  get orderToBilling() {
    return this.order?.extra?.orderToBilling ?? false
  }

  // Progress for image pull (type=1) or snapshot load (type=11) during Pending state
  get progress(): number | undefined {
    if (this.status !== InstanceStatus.Pending) return undefined
    return this.eventLog?.events?.[0]?.pullEv?.progress
  }
}

export type HardwareInfo = {
  priceMillicent: number
  machine: MachineInfo
  gpu: GpuInfo
  deadlineTime: DeadlineTime
  discountPriceMillicent: number
  timeLimitSec: number
  attributes: Attributes
  maxRef: number
  vgpu?: any
  maxRefLimit: number
}

interface MachineInfo {
  cpuIds: number[]
  cpuName: string
  ramMB: number
  diskGB: number
  diskName: string
  diskBandwidthMBs: number
  motherboardName: string
  pcieVer: string
  internetUploadMbs: number
  internetDownloadMbs: number
  totalCPUCores: number
  totalRAMMB: number
  bandwidthBit: number
  totalBandwidthBit: number
  arch: number
  pciConfig?: any
  unitConfig?: any
  gpuResourceLimit?: any
}

interface GpuInfo {
  gpuIds: number[]
  gpuName: string
  gpuRamMB: number
  gpuMemBandwidthGBs: number
  gpuPCIELanes: number
  gpuPCIEBandwidthGBs: number
  maxCUDAVer?: any
  tflops: number
  driver?: any
  gpuModel?: any
  uuids?: any
  vendor: number
}

interface DeadlineTime {
  seconds: number
}

interface Attributes {
  isSelfOwner: boolean
  isThirdParty: boolean
  isSpecialPrice: boolean
  isBetaTest: boolean
  isXEnabled: boolean
  isSnapEnabled: boolean
  isComposable: boolean
  isVGPU: boolean
  isMIG: boolean
  isDistributed: boolean
  isHyperContainer: boolean
  isWinEnabled: boolean
  isSupportNodeOp: boolean
  isECS: boolean
  isTimeSliced: boolean
  isMPU: boolean
}

export type UsageState = {
  cpuUtilPercent: number
  diskUsageGB: number
  ramUsageMB: number
  gpuState: Array<GpuState>
}

type GpuState = {
  gpuRamUsageMB: number
  gpuUtilPercent: number
  gpuID: number
}

export interface NetGroup {
  netResID: string
  networkName: string
  edpResID: string
  cidr: string
  mac: string
  isAvail: boolean
}
